import { AI_DISCLOSURE, type LegalPageId, legalDocument, legalDocuments } from "@app/shared";
import { Link } from "@tanstack/react-router";
import { appConfig } from "../../../../app.config";
import { READING_WIDTH } from "../lib/layout";

/**
 * Pages legales et mentions obligatoires. Le texte vient de `@app/shared`, construit
 * a partir de `app.config.ts` : ces composants ne font que le mettre en page.
 *
 * Ils appartiennent au template. Pour changer ce qui est ecrit, on remplit
 * `app.config.ts` ; pour changer le texte lui-meme, on ouvre une PR sur le template,
 * et toutes les apps du Lab en profitent.
 */

function IdentityWarning({ missing }: { missing: string[] }) {
  return (
    <aside
      className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm"
      role="alert"
    >
      <p className="font-semibold">Identité de l'éditeur incomplète</p>
      <p className="mt-1">
        Cette page ne peut pas être publiée en l'état. Renseignez ces champs dans
        <code className="mx-1">app.config.ts</code>:
      </p>
      <ul className="mt-2 list-disc pl-5">
        {missing.map((field) => (
          <li key={field}>
            <code>{field}</code>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export function LegalPage({ id }: { id: LegalPageId }) {
  const document = legalDocument(appConfig, id);
  if (!document) {
    return (
      <section className={READING_WIDTH}>
        <h1 className="text-xl font-semibold">Page non publiée</h1>
        <p className="mt-2 text-muted-foreground">Cette page ne concerne pas {appConfig.name}.</p>
      </section>
    );
  }

  return (
    <article className={`flex flex-col gap-8 ${READING_WIDTH}`}>
      <header>
        <h1 className="text-2xl font-semibold">{document.title}</h1>
        {document.updatedAt ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Dernière mise à jour : {document.updatedAt}
          </p>
        ) : null}
      </header>

      {document.missing.length > 0 ? <IdentityWarning missing={document.missing} /> : null}

      {document.sections.map((section) => (
        <section key={section.heading} className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{section.heading}</h2>
          {section.paragraphs.map((paragraph, rank) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: paragraphes figes, jamais reordonnes ; le texte seul ne ferait pas une cle, deux traitements de l'app peuvent le partager
            <p key={rank} className="text-sm leading-relaxed text-muted-foreground">
              {paragraph}
            </p>
          ))}
        </section>
      ))}
    </article>
  );
}

/** Liens vers les pages legales, poses dans le pied de page par le template. */
export function LegalLinks() {
  return (
    <nav aria-label="Informations légales" className="flex flex-wrap gap-x-4 gap-y-1">
      {legalDocuments(appConfig).map((document) => (
        <Link key={document.id} to={document.path} className="underline underline-offset-2">
          {document.title}
        </Link>
      ))}
      <Link to="/mes-donnees" className="underline underline-offset-2">
        Mes données
      </Link>
    </nav>
  );
}

/**
 * Transparence de l'article 50 du reglement europeen sur l'IA : quand une app genere
 * du contenu, elle le dit, a l'ecran, sans avoir a ouvrir une page legale.
 */
export function AiNotice() {
  if (!appConfig.features.llm) return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="ai-notice">
      {AI_DISCLOSURE}
    </p>
  );
}
